import type { ReactNode } from "react";
import { Tag, type TagProps } from "zhihao-ui-react";

export type StatusTone = "primary" | "success" | "info" | "warning" | "danger" | "neutral";
export type StatusAppearance = "plain" | "tag";

export interface StatusTagProps extends Omit<TagProps, "type"> {
    /** 显式语义优先于文案推断，业务枚举应尽量传入该字段。 */
    tone?: StatusTone;
    /** 旧 Vue 默认输出普通文本，只有旧版明确使用 el-tag 时才传入 tag。 */
    appearance?: StatusAppearance;
}

const STATUS_TONE_RULES: Array<{ tone: Exclude<StatusTone, "neutral">; pattern: RegExp }> = [
    { tone: "success", pattern: /正常|启用|有效|成功|完成|通过|在线|已支付|已处理|已上架|(?<!未)发布|是$/i },
    { tone: "danger", pattern: /失败|异常|拒绝|禁用|删除|失效|错误|下线|否$/i },
    { tone: "warning", pattern: /待|处理中|处理|审核|跟进|等待|进行中|部分/i },
    { tone: "info", pattern: /取消|关闭|草稿|未标记|未关联|未知|无效/i }
];

/** 将 React 文本节点转换为可用于状态推断的字符串。 */
function getNodeText(node: ReactNode): string {
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(getNodeText).join("");
    return "";
}

/** 根据常见中文状态文案提供迁移期兜底语义，避免无类型 Tag 回退为黑色普通标签。 */
export function getStatusTone(value: ReactNode): StatusTone {
    const text = getNodeText(value).trim();
    return STATUS_TONE_RULES.find(rule => rule.pattern.test(text))?.tone ?? "neutral";
}

/** 将旧枚举中的历史颜色名称归一到共享状态类型。 */
export function normalizeStatusTone(value: unknown): StatusTone | undefined {
    if (value == null || value === "") return undefined;
    if (value === "error" || value === "red") return "danger";
    if (value === "green") return "success";
    if (value === "blue" || value === "processing") return "primary";
    if (value === "gray" || value === "grey" || value === "default") return "info";
    if (value === "primary" || value === "success" || value === "info" || value === "warning" || value === "danger") {
        return value;
    }
    return undefined;
}

/** 全局状态展示入口，统一状态语义、尺寸和可测试 class。 */
export function StatusTag({
    tone,
    children,
    className,
    effect = "light",
    appearance = "plain",
    ...restProps
}: StatusTagProps) {
    const resolvedTone = tone ?? getStatusTone(children);
    const uiType = resolvedTone === "neutral" ? "info" : resolvedTone;
    const mergedClassName = ["admin-status-tag", `admin-status-tag--${resolvedTone}`, className]
        .filter(Boolean)
        .join(" ");

    if (appearance === "plain") {
        const textClassName = ["admin-status-text", className].filter(Boolean).join(" ");
        return (
            <span {...restProps} className={textClassName}>
                {children}
            </span>
        );
    }

    return (
        <Tag {...restProps} className={mergedClassName} effect={effect} type={uiType}>
            {children}
        </Tag>
    );
}

export default StatusTag;
